import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Truck, Receipt, TrendingUp, LogOut, Package, Clock, CheckCircle, AlertTriangle, IndianRupee, FileText, Users, Target, ShoppingCart, Megaphone, AlertOctagon, Scale, CreditCard, Settings, Shield, Key, Pen } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import { SoundSettings } from '../components/SoundSettings';
import { playWelcomeTone, getTodayFestival, playNotificationAlert } from '../utils/welcomeTones';
import SignatureUpload from '../components/SignatureUpload';
import { PasskeyManager, LogoutSafetyDialog, needsFaceSetupBeforeLogout } from '../components/Passkey';
import PlatesHomeCard from '../components/PlatesHomeCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = ({ setAuth }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [logisticsStats, setLogisticsStats] = useState({
    total: 0,
    pending: 0,
    inTransit: 0,
    completed: 0
  });
  const [expenseStats, setExpenseStats] = useState({
    total: 0,
    draft: 0,
    pending: 0,
    approved: 0,
    total_amount: 0
  });
  const [shortageStats, setShortageStats] = useState({
    total_shortages: 0,
    pending_claims: 0,
    resolved_claims: 0,
    total_amount: 0
  });
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [division, setDivision] = useState(localStorage.getItem('jmepl_division') || 'pig_iron');

  const getDivisions = (u) => (u?.divisions?.length ? u.divisions : (['admin', 'owner'].includes(u?.role) ? ['pig_iron', 'plates'] : ['pig_iron']));

  const switchDivision = (d) => {
    setDivision(d);
    localStorage.setItem('jmepl_division', d);
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/login');
      return;
    }
    const parsed = JSON.parse(userData);
    setUser(parsed);
    // Refresh from server so division rights are current
    axios.get(`${API}/auth/me`, { withCredentials: true }).then(r => {
      setUser(r.data);
      localStorage.setItem('user', JSON.stringify(r.data));
      const divs = r.data?.divisions?.length ? r.data.divisions : (['admin', 'owner'].includes(r.data?.role) ? ['pig_iron', 'plates'] : ['pig_iron']);
      const saved = localStorage.getItem('jmepl_division') || 'pig_iron';
      if (!divs.includes(saved)) {
        setDivision(divs[0]);
        localStorage.setItem('jmepl_division', divs[0]);
      }
    }).catch(() => {});
    fetchLogisticsStats();
    fetchExpenseStats();
    fetchShortageStats();
    fetchMarketingStats();
    // Prefetch unread count NOW so the alert plays exactly 3s after the greeting ends
    const unreadPromise = axios.get(`${API}/notifications/unread-count`, { withCredentials: true })
      .then(r => r.data?.count || 0).catch(() => 0);
    playWelcomeTone(parsed?.full_name, async () => {
      const count = await unreadPromise;
      if (count > 0) {
        await new Promise(r => setTimeout(r, 3000));
        playNotificationAlert();
        toast.info(`🔔 You have ${count} unread notification${count > 1 ? 's' : ''}`, { duration: 6000 });
      }
    });
    const festival = getTodayFestival();
    if (festival && !sessionStorage.getItem('jmepl_festival_toasted')) {
      sessionStorage.setItem('jmepl_festival_toasted', '1');
      toast(`✨ Happy ${festival}! Wishing you and your family a wonderful celebration.`, { duration: 8000 });
    }
  }, [navigate]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        withCredentials: true
      });
      
      toast.success('Password changed successfully');
      setIsPasswordDialogOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const fetchLogisticsStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`, {
        withCredentials: true
      });
      setLogisticsStats({
        total: response.data.total || 0,
        pending: response.data.pending_rollover || 0,
        inTransit: response.data.trucks_assigned || 0,
        completed: response.data.completed || 0
      });
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchShortageStats = async () => {
    try {
      const response = await axios.get(`${API}/shortage-stats`, {
        withCredentials: true
      });
      setShortageStats(response.data);
    } catch (error) {
      console.error('Failed to fetch shortage stats');
    }
  };

  const fetchExpenseStats = async () => {
    try {
      const response = await axios.get(`${API}/expense-vouchers-stats`, {
        withCredentials: true
      });
      setExpenseStats({
        total: response.data.total || 0,
        draft: response.data.draft || 0,
        pending: response.data.pending || 0,
        approved: response.data.approved || 0,
        total_amount: response.data.total_amount || 0
      });
    } catch (error) {
      console.error('Failed to fetch expense stats');
    }
  };

  const [logoutGuard, setLogoutGuard] = useState(false);
  const doLogout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('jmepl_welcome_played');
    setAuth?.(false);
    navigate('/login');
  };
  const handleLogout = () => (needsFaceSetupBeforeLogout() ? setLogoutGuard(true) : doLogout());

  // Marketing card stats (real numbers)
  const [marketingStats, setMarketingStats] = useState({
    po_pending: 0,
    so_pending: 0,
    quotations_sent: 0,
    follow_ups_due: 0
  });

  const fetchMarketingStats = async () => {
    try {
      const response = await axios.get(`${API}/marketing/home-stats`, { withCredentials: true });
      setMarketingStats(response.data);
    } catch (error) {
      console.error('Failed to fetch marketing stats');
    }
  };

  const salesStats = {
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    revenue: '₹0'
  };

  // shortageStats is now fetched from API (defined above in useState)
  const paymentStats = {
    totalPayments: 0,
    pendingApproval: 0,
    approved: 0,
    totalAmount: '₹0'
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 safe-area-top safe-area-bottom">
      <LogoutSafetyDialog open={logoutGuard} onClose={() => setLogoutGuard(false)} onLogout={doLogout} />
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-40">
        <div className="px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <img 
              src="/logo.png" 
              alt="JMEPL Logo" 
              className="h-8 sm:h-12 w-auto flex-shrink-0"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="min-w-0">
              <h1 className="text-sm sm:text-xl font-bold text-slate-900 truncate">JANARDAN MINERAL EXIM PVT LTD</h1>
              <p className="text-[10px] sm:text-xs text-orange-600 font-semibold truncate hidden sm:block">INDIA'S ONE OF THE BIGGEST SUPPLIER FOR PIG IRON</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <SoundSettings />
            <NotificationBell />
            <Button
              variant="outline"
              onClick={() => setIsSignatureDialogOpen(true)}
              data-testid="my-signature-button"
              className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4 touch-target text-green-700 border-green-300 hover:bg-green-50"
            >
              <Pen className="w-4 h-4" />
              <span className="hidden sm:inline">My Signature</span>
            </Button>
            <SignatureUpload isOpen={isSignatureDialogOpen} onClose={() => setIsSignatureDialogOpen(false)} />
            <PasskeyManager compact />
            <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="change-password-button"
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4 touch-target"
                >
                  <Key className="w-4 h-4" />
                  <span className="hidden sm:inline">Password</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] sm:max-w-md mx-2">
                <DialogHeader>
                  <DialogTitle className="text-base sm:text-lg">Change Password</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleChangePassword} className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
                  <div>
                    <Label htmlFor="current_password" className="text-sm">Current Password</Label>
                    <Input
                      id="current_password"
                      type="password"
                      data-testid="input-current-password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      required
                      className="mt-1 h-10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new_password" className="text-sm">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      data-testid="input-new-password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      required
                      minLength={6}
                      className="mt-1 h-10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm_password" className="text-sm">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      data-testid="input-confirm-password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      required
                      minLength={6}
                      className="mt-1 h-10"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-slate-900 hover:bg-slate-800 h-11 touch-target"
                    disabled={passwordLoading}
                    data-testid="submit-change-password"
                  >
                    {passwordLoading ? 'Changing...' : 'Change Password'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <div className="text-right hidden sm:block">
              <p className="text-sm text-slate-500">Logged in as</p>
              <p className="text-base font-bold text-slate-900">{user?.full_name || user?.username}</p>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4 touch-target"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-3 sm:px-6 py-4 sm:py-8 max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="text-center mb-6 sm:mb-10">
          <h1 className="text-xl sm:text-3xl font-bold text-slate-900 mb-1 sm:mb-2">
            Welcome, <span className="text-blue-600">{user?.full_name || user?.username}</span>!
          </h1>
          <p className="text-sm sm:text-base text-slate-500">
            Select a module below to continue
          </p>
          {getDivisions(user).length > 1 && (
            <div className="inline-flex items-center gap-1 mt-3 bg-white border border-slate-200 rounded-full p-1 shadow-sm" data-testid="division-switcher">
              <button onClick={() => switchDivision('pig_iron')} data-testid="division-pig-iron"
                className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${division === 'pig_iron' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                Pig Iron
              </button>
              <button onClick={() => switchDivision('plates')} data-testid="division-plates"
                className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${division === 'plates' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                Plates Trading
              </button>
            </div>
          )}
        </div>

        {(division === 'plates' || !getDivisions(user).includes('pig_iron')) ? (
          <PlatesHomeCard />
        ) : (
        <>
        {/* Module Cards with Dashboards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          
          {/* Logistics Solution Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-blue-200 shadow-lg overflow-hidden">
            <div className="bg-blue-500 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
                    <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h2 className="text-base sm:text-xl font-bold text-white">Logistics Solution</h2>
                </div>
              </div>
            </div>
            
            {/* Logistics Stats */}
            <div className="p-3 sm:p-4">
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="bg-slate-50 rounded-lg p-2 sm:p-3 text-center">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-slate-900">{logisticsStats.total}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total Requirements</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 text-center">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-yellow-600">{logisticsStats.pending}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Pending</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-2 sm:p-3 text-center">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-orange-600">{logisticsStats.inTransit}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">In Transit</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-green-600">{logisticsStats.completed}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Completed</p>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white touch-target text-sm sm:text-base"
                data-testid="module-logistics"
              >
                Enter Logistics
              </Button>
            </div>
          </div>

          {/* Expense Voucher Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-green-200 shadow-lg overflow-hidden">
            <div className="bg-green-500 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
                    <Receipt className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h2 className="text-base sm:text-xl font-bold text-white">Expense Voucher</h2>
                </div>
              </div>
            </div>
            
            {/* Expense Stats */}
            <div className="p-3 sm:p-4">
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="bg-slate-50 rounded-lg p-2 sm:p-3 text-center">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-slate-900">{expenseStats.total}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total Vouchers</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 text-center">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-yellow-600">{expenseStats.pending}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Pending Approval</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-green-600">{expenseStats.approved}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Approved</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 sm:p-3 text-center">
                  <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-blue-600">₹{expenseStats.total_amount?.toLocaleString() || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total Amount</p>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/expense')}
                className="w-full bg-green-500 hover:bg-green-600 text-white touch-target text-sm sm:text-base"
                data-testid="module-expense"
              >
                Enter Expense
              </Button>
            </div>
          </div>

          {/* Marketing Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-purple-200 shadow-lg overflow-hidden">
            <div className="bg-purple-500 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
                    <Megaphone className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h2 className="text-base sm:text-xl font-bold text-white">Marketing</h2>
                </div>
              </div>
            </div>
            
            {/* Marketing Stats */}
            <div className="p-3 sm:p-4">
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="bg-slate-50 rounded-lg p-2 sm:p-3 text-center cursor-pointer hover:ring-2 hover:ring-purple-300 transition-shadow" onClick={() => navigate('/marketing?tab=purchase-orders')} data-testid="mk-stat-po">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-slate-900">{marketingStats.po_pending}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total Purchase Order Pending</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 text-center cursor-pointer hover:ring-2 hover:ring-yellow-300 transition-shadow" onClick={() => navigate('/marketing?tab=sales-orders')} data-testid="mk-stat-so">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-yellow-600">{marketingStats.so_pending}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total Sales Order Pending</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center cursor-pointer hover:ring-2 hover:ring-green-300 transition-shadow" onClick={() => navigate('/marketing?tab=quotation')} data-testid="mk-stat-quote">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-green-600">{marketingStats.quotations_sent}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total Quotation Sent</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 sm:p-3 text-center cursor-pointer hover:ring-2 hover:ring-blue-300 transition-shadow" onClick={() => navigate('/marketing?tab=order-tracker')} data-testid="mk-stat-followup">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-blue-600">{marketingStats.follow_ups_due}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Order Tracker Follow-Up</p>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/marketing')}
                className="w-full bg-purple-500 hover:bg-purple-600 text-white touch-target text-sm sm:text-base"
                data-testid="module-marketing"
              >
                Enter Marketing
              </Button>
            </div>
          </div>

          {/* Sales Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-orange-200 shadow-lg overflow-hidden">
            <div className="bg-orange-500 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
                    <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h2 className="text-base sm:text-xl font-bold text-white">Sales</h2>
                </div>
              </div>
            </div>
            
            {/* Sales Stats */}
            <div className="p-3 sm:p-4">
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="bg-slate-50 rounded-lg p-2 sm:p-3 text-center">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-slate-900">{salesStats.totalOrders}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total Orders</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 text-center">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-yellow-600">{salesStats.pendingOrders}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Pending Orders</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-green-600">{salesStats.completedOrders}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Completed Orders</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 sm:p-3 text-center">
                  <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-blue-600">{salesStats.revenue}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Revenue</p>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/sales')}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white touch-target text-sm sm:text-base"
                data-testid="module-sales"
              >
                Enter Sales
              </Button>
            </div>
          </div>

          {/* Shortages Details Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-red-200 shadow-lg overflow-hidden">
            <div className="bg-red-500 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
                    <Scale className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h2 className="text-base sm:text-xl font-bold text-white">Shortages Details</h2>
                </div>
              </div>
            </div>
            
            {/* Shortage Stats */}
            <div className="p-3 sm:p-4">
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="bg-slate-50 rounded-lg p-2 sm:p-3 text-center">
                  <AlertOctagon className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-slate-900">{shortageStats.total_shortages}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total Shortages</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 text-center">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-yellow-600">{shortageStats.pending_claims}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Pending Claims</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-green-600">{shortageStats.resolved_claims}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Resolved Claims</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 sm:p-3 text-center">
                  <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-blue-600">₹{shortageStats.total_amount?.toLocaleString('en-IN') || 0}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total Amount</p>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/shortages')}
                className="w-full bg-red-500 hover:bg-red-600 text-white touch-target text-sm sm:text-base"
                data-testid="module-shortages"
              >
                Enter Shortages
              </Button>
            </div>
          </div>

          {/* Payment Approval Details Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-indigo-200 shadow-lg overflow-hidden">
            <div className="bg-indigo-500 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
                    <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h2 className="text-base sm:text-xl font-bold text-white">Payment Approval</h2>
                </div>
              </div>
            </div>
            
            {/* Payment Stats */}
            <div className="p-3 sm:p-4">
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="bg-slate-50 rounded-lg p-2 sm:p-3 text-center">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-slate-900">{paymentStats.totalPayments}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total Payments</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 text-center">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-yellow-600">{paymentStats.pendingApproval}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Pending Approval</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-green-600">{paymentStats.approved}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Approved</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 sm:p-3 text-center">
                  <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-lg sm:text-2xl font-bold text-blue-600">{paymentStats.totalAmount}</p>
                  <p className="text-[10px] sm:text-xs text-slate-500">Total Amount</p>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate('/payments')}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white touch-target text-sm sm:text-base"
                data-testid="module-payments"
              >
                Enter Payments
              </Button>
            </div>
          </div>

          {/* Godown Management Card */}
          <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-amber-200 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
                    <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <h2 className="text-base sm:text-xl font-bold text-white">Godown Management</h2>
                </div>
              </div>
            </div>
            
            <div className="p-3 sm:p-4">
              <p className="text-slate-600 text-xs sm:text-sm mb-3 sm:mb-4 text-center">
                Manage warehouse stock, inbound & outbound transactions
              </p>
              
              <Button 
                onClick={() => navigate('/godown')}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white touch-target text-sm sm:text-base"
                data-testid="module-godown"
              >
                Enter Godown
              </Button>
            </div>
          </div>

        </div>

        {/* Admin Panel Card - Only visible to Admin */}
        {user?.role === 'admin' && (
          <div className="mt-6 sm:mt-8">
            <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-slate-300 shadow-lg overflow-hidden max-w-md mx-auto">
              <div className="bg-slate-700 p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg">
                      <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <h2 className="text-base sm:text-xl font-bold text-white">Admin Panel</h2>
                  </div>
                </div>
              </div>
              
              <div className="p-3 sm:p-4">
                <p className="text-slate-600 text-xs sm:text-sm mb-3 sm:mb-4 text-center">
                  Manage users, grades, purchase orders, and system settings
                </p>
                
                <Button 
                  onClick={() => navigate('/admin')}
                  className="w-full bg-slate-700 hover:bg-slate-800 text-white touch-target text-sm sm:text-base"
                  data-testid="module-admin"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Enter Admin Panel
                </Button>
              </div>
            </div>
          </div>
        )}

        </>
        )}

        {/* Footer */}
        <div className="mt-8 sm:mt-12 text-center text-slate-400 text-xs sm:text-sm pb-4">
          <p>© 2026 Janardan Mineral Exim Pvt Ltd. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};

export default Home;
